import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getSurveyLinksData,
  getSurveyLinksSections,
  getSurveyLinksQuestions,
  getSurveyLinksPanels,
  SurveyLinksData,
  SurveyLinksSection,
  SurveyLinksQuestion,
  SurveyLinksPanelData,
  SurveyLinksSettings,
} from '../asyncActions/surveylinks';

interface SurveyLinksState {
  // Tier 1 data
  linksData: SurveyLinksData | null;
  linksLoading: boolean;
  linksLoaded: boolean;

  // Tier 2a data
  sections: SurveyLinksSection[];
  // Respondent dashboard link; null unless the survey has 2+ sections.
  sectionsDashboardUrl: string | null;
  sectionsLoading: boolean;
  sectionsLoaded: boolean;

  // Tier 2b data
  questions: SurveyLinksQuestion[];
  questionsLoading: boolean;
  questionsLoaded: boolean;

  // Tier 2c data
  panelData: SurveyLinksPanelData | null;
  panelsLoading: boolean;
  panelsLoaded: boolean;

  // Active tab
  activeTab: string;

  // Error state
  error: string | null;
}

const initialState: SurveyLinksState = {
  linksData: null,
  linksLoading: false,
  linksLoaded: false,

  sections: [],
  sectionsDashboardUrl: null,
  sectionsLoading: false,
  sectionsLoaded: false,

  questions: [],
  questionsLoading: false,
  questionsLoaded: false,

  panelData: null,
  panelsLoading: false,
  panelsLoaded: false,

  activeTab: 'share-link',

  error: null,
};

const surveylinksSlice = createSlice({
  name: 'surveylinks',
  initialState,
  reducers: {
    setActiveTab: (state, action: PayloadAction<string>) => {
      state.activeTab = action.payload;
    },
    updateSettings: (state, action: PayloadAction<Partial<SurveyLinksSettings>>) => {
      if (state.linksData) {
        state.linksData.settings = {
          ...state.linksData.settings,
          ...action.payload,
        };
      }
    },
    // Local optimistic section patch. Merges any subset of the mutable
    // fields (`isOn`, `startAt`, `endAt`, `timezone`) on the matched
    // section so the UI reflects the write before the PUT resolves.
    // Callers revert by dispatching again with the pre-write snapshot.
    updateSectionLocal: (
      state,
      action: PayloadAction<{
        sectionOrder: number;
        isOn?: boolean;
        startAt?: string | null;
        endAt?: string | null;
        timezone?: string | null;
      }>,
    ) => {
      const section = state.sections.find(
        (s) => s.sectionOrder === action.payload.sectionOrder,
      );
      if (!section) return;
      const { isOn, startAt, endAt, timezone } = action.payload;
      if (isOn !== undefined) section.isOn = isOn;
      if (startAt !== undefined) section.startAt = startAt;
      if (endAt !== undefined) section.endAt = endAt;
      if (timezone !== undefined) section.timezone = timezone;
    },
    resetSurveyLinks: () => initialState,
  },
  extraReducers: (builder) => {
    // Tier 1: Links data
    builder
      .addCase(getSurveyLinksData.pending, (state) => {
        state.linksLoading = true;
        state.error = null;
      })
      .addCase(getSurveyLinksData.fulfilled, (state, action) => {
        state.linksLoading = false;
        state.linksLoaded = true;
        state.linksData = action.payload.value;
      })
      .addCase(getSurveyLinksData.rejected, (state, action) => {
        state.linksLoading = false;
        state.error = action.error.message || 'Failed to load links data';
      });

    // Tier 2a: Sections
    builder
      .addCase(getSurveyLinksSections.pending, (state) => {
        state.sectionsLoading = true;
        state.error = null;
      })
      .addCase(getSurveyLinksSections.fulfilled, (state, action) => {
        state.sectionsLoading = false;
        state.sectionsLoaded = true;
        state.sections = action.payload.value?.sections || [];
        state.sectionsDashboardUrl = action.payload.value?.dashboardUrl ?? null;
      })
      .addCase(getSurveyLinksSections.rejected, (state, action) => {
        state.sectionsLoading = false;
        state.error = action.error.message || 'Failed to load sections';
      });

    // Tier 2b: Questions
    builder
      .addCase(getSurveyLinksQuestions.pending, (state) => {
        state.questionsLoading = true;
        state.error = null;
      })
      .addCase(getSurveyLinksQuestions.fulfilled, (state, action) => {
        state.questionsLoading = false;
        state.questionsLoaded = true;
        state.questions = action.payload.value?.questions || [];
      })
      .addCase(getSurveyLinksQuestions.rejected, (state, action) => {
        state.questionsLoading = false;
        state.error = action.error.message || 'Failed to load questions';
      });

    // Tier 2c: Panels
    builder
      .addCase(getSurveyLinksPanels.pending, (state) => {
        state.panelsLoading = true;
        state.error = null;
      })
      .addCase(getSurveyLinksPanels.fulfilled, (state, action) => {
        state.panelsLoading = false;
        state.panelsLoaded = true;
        state.panelData = action.payload.value;
      })
      .addCase(getSurveyLinksPanels.rejected, (state, action) => {
        state.panelsLoading = false;
        state.error = action.error.message || 'Failed to load panel data';
      });
  },
});

export const {
  setActiveTab,
  updateSettings,
  updateSectionLocal,
  resetSurveyLinks,
} = surveylinksSlice.actions;

export default surveylinksSlice.reducer;
